import React, { useEffect, useState } from "react";
import { FaRegEdit } from "react-icons/fa";
import { IoInformationCircleOutline } from "react-icons/io5";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { LuPhone } from "react-icons/lu";
import { CgProfile } from "react-icons/cg";
import { SlCalender } from "react-icons/sl";
import { BsExclamationCircle } from "react-icons/bs";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [adminData, setAdminData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    gender: "",
    dateOfBirth: "",
    profilePic: "",
  });

  const [originalData, setOriginalData] = useState(null);

  const getAdminData = () => {
    const data = localStorage.getItem("adminData");
    return data ? JSON.parse(data) : null;
  };

  // Fetch admin details
  useEffect(() => {
    const admin = getAdminData();

    if (admin && admin._id) {
      setAdminData(admin);
      const initialFormData = {
        name: admin.name || "",
        email: admin.email || "",
        phoneNumber: admin.phoneNumber || "",
        gender: admin.gender || "",
        dateOfBirth: admin.dateOfBirth?.slice(0, 10) || "",
        profilePic: admin.profilePic || "https://i.pravatar.cc/150?img=47",
      };
      setFormData(initialFormData);
      setOriginalData(initialFormData);

      // Try fetching latest profile from server
      fetchAdminData(admin._id);
    } else {
      setError("No admin data found. Please login again.");
      setLoading(false);
      toast.error("No admin data found. Please login again.");
    }
  }, []);

  const fetchAdminData = async (adminId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admins/admin-by-id/${adminId}`
      );
      const data = res.data.data;

      setAdminData(data);
      const updatedFormData = {
        name: data.name || "",
        email: data.email || "",
        phoneNumber: data.phoneNumber || "",
        gender: data.gender || "",
        dateOfBirth: data.dateOfBirth?.slice(0, 10) || "",
        profilePic: data.profilePic || "https://i.pravatar.cc/150?img=47",
      };
      setFormData(updatedFormData);
      setOriginalData(updatedFormData);
      
      // toast.success("Profile data loaded successfully!");
    } catch (err) {
      console.error("Profile Fetch Error:", err);
      setError("Unable to fetch latest profile data.");
      toast.error("Unable to fetch latest profile data.");
    } finally {
      setLoading(false);
    }
  };

  // Check if form has changes
  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  // Save updated profile
  const handleSave = async () => {
    if (!adminData || !adminData._id) return;

    // Validate required fields
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Name and email are required fields.");
      return;
    }

    setSaving(true);
    
    const savePromise = axios.put(
      `${process.env.REACT_APP_API_URL}/admins/edit-admin/${adminData._id}`,
      formData
    );

    toast.promise(
      savePromise,
      {
        loading: 'Updating profile...',
        success: (response) => {
          const updatedAdmin = { ...adminData, ...formData };
          setAdminData(updatedAdmin);
          setOriginalData(formData);
          localStorage.setItem("adminData", JSON.stringify(updatedAdmin));
          setIsEditing(false);
          return 'Profile updated successfully!';
        },
        error: (err) => {
          console.error("Update Error:", err);
          return err.response?.data?.message || 'Failed to update profile. Please try again.';
        },
      }
    );

    try {
      await savePromise;
    } catch (err) {
      // Error is handled by toast.promise
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges()) {
      if (window.confirm("You have unsaved changes. Are you sure you want to cancel?")) {
        setFormData(originalData);
        setIsEditing(false);
        toast.error("Changes discarded");
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const onlyDate = (date) => {
    if (!date) return "Not set";
    return date.split("-").reverse().join("-");
  };

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600 text-sm">Loading profile...</div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EEF3F7] p-6">
        <div className="bg-white shadow-lg border border-red-200 rounded-lg p-6 max-w-sm text-center">
          <div className="flex justify-center mb-3">
            <BsExclamationCircle className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Profile
          </h3>
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main UI
  return (
    <div className="min-h-screen bg-[#EEF3F7] p-6">
      <div className="w-full mx-auto space-y-6 ">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={formData.profilePic}
                  alt="Admin Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-blue-300 shadow-md"
                />
                {isEditing && (
                  <button
                    onClick={() => toast("Profile picture update feature coming soon!", { icon: '🔄' })}
                    className="absolute bottom-0 right-0 bg-blue-600 text-white p-1 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaRegEdit className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="flex-1">
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-lg font-semibold w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your name"
                  />
                ) : (
                  <h1 className="text-3xl font-philosopher text-gray-900">
                    {formData.name}
                  </h1>
                )}
                <p className="text-gray-500 mt-1">{formData.email}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <div className="bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-md">
                    {adminData?.userType || "Admin"}
                  </div>
                  {hasChanges() && isEditing && (
                    <div className="bg-yellow-100 text-yellow-700 text-xs font-medium px-3 py-1 rounded-md">
                      Unsaved Changes
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow hover:shadow-lg transition-all flex items-center gap-2 hover:scale-105"
                >
                  <FaRegEdit className="w-4 h-4" />
                  Edit Profile
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <IoInformationCircleOutline className="text-blue-600 w-5 h-5" />
            Personal Information
          </h2>
          
          <p className="text-gray-600 text-sm leading-relaxed bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
            Welcome to your Admin Panel! Here you can manage your e-commerce account, 
            update your information, and maintain a professional profile.
          </p>

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <MdOutlineAlternateEmail className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700">Email Address</h3>
              </div>
              {isEditing ? (
                <input
                  type="email"
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                />
              ) : (
                <p className="text-gray-800 font-medium">{formData.email}</p>
              )}
            </div>

            {/* Phone */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <LuPhone className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700">Phone Number</h3>
              </div>
              {isEditing ? (
                <input
                  type="tel"
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                />
              ) : (
                <p className="text-gray-800 font-medium">{formData.phoneNumber || "Not set"}</p>
              )}
            </div>

            {/* Gender */}
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <CgProfile className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700">Gender</h3>
              </div>
              {isEditing ? (
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              ) : (
                <p className="text-gray-800 font-medium capitalize">
                  {formData.gender || "Not set"}
                </p>
              )}
            </div>

            {/* DOB */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <SlCalender className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700">Date of Birth</h3>
              </div>
              {isEditing ? (
                <input
                  type="date"
                  className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                />
              ) : (
                <p className="text-gray-800 font-medium">
                  {onlyDate(formData.dateOfBirth)}
                </p>
              )}
            </div>
          </div>

          {/* Additional Info */}
          {/* {adminData && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 text-sm mb-1">Admin ID</h4>
                <p className="text-gray-600 text-sm font-mono">{adminData._id}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 text-sm mb-1">Account Type</h4>
                <p className="text-gray-600 text-sm capitalize">{adminData.userType || "Administrator"}</p>
              </div>
            </div>
          )} */}
        </div>

        {/* Quick Stats */}
        {/* <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              onClick={() => toast.success("Password reset link sent to your email!")}
              className="p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <h3 className="font-semibold text-blue-700">Change Password</h3>
              <p className="text-sm text-gray-600 mt-1">Update your account password</p>
            </button>
            
            <button 
              onClick={() => toast("Notification preferences page coming soon!", { icon: '🔔' })}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors text-left"
            >
              <h3 className="font-semibold text-green-700">Notifications</h3>
              <p className="text-sm text-gray-600 mt-1">Manage your notification settings</p>
            </button>
            
            <button 
              onClick={() => toast("Privacy settings page coming soon!", { icon: '🛡️' })}
              className="p-4 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors text-left"
            >
              <h3 className="font-semibold text-purple-700">Privacy</h3>
              <p className="text-sm text-gray-600 mt-1">Control your privacy settings</p>
            </button>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Profile;